import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { selectors as usersListSelectors } from '../../../slices/users/list';
import { RootState } from '../../../slices/rootReducer';
import { IUser } from '../../../slices/users/user';
import { RemoveUnusedSeatsModal } from './RemoveUnusedSeatsModal';
import { RemoveUsedSeatsModal } from './RemoveUsedSeats';
import { ConfirmRemovalModal } from './ConfirmRemovalModal';
import { CadenceTypesValues } from '../../auth/proRegister/subscriptionTypes';

interface ILocalState {
  selectedSeatsToRemove: number;
  neededUsersToSelect: number;
  usersToRemove: string[];
  confirmingRemoval: boolean;
}

interface IMapStateProps {
  seatsPurchased: number;
  seatsUsed: number;
  users: IUser[];
  cycleEndDate: number;
  cadence: CadenceTypesValues;
  alreadyRemovedUsers: string[];
}

interface IExternalProps {
  visible: boolean;
  accountId: string;
  onCancel: () => void;
}

type Props = IMapStateProps & IExternalProps;

class RemovePaidSeatsBase extends Component<Props, ILocalState> {

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedSeatsToRemove: 1,
      neededUsersToSelect: 0,
      usersToRemove: [],
      confirmingRemoval: false
    };
    this.handleCancel = this.handleCancel.bind(this);
    this.selectUsersToRemove = this.selectUsersToRemove.bind(this);
    this.handleRequireConfirmation = this.handleRequireConfirmation.bind(this);
  }

  private handleCancel() {
    this.setState({
      selectedSeatsToRemove: 1,
      neededUsersToSelect: 0,
      confirmingRemoval: false,
      usersToRemove: []
    });
    this.props.onCancel();
  }

  private selectUsersToRemove(selectedSeatsToRemove: number) {
    const { seatsPurchased, seatsUsed } = this.props;
    const unusedSeats = seatsPurchased - seatsUsed;
    this.setState({
      selectedSeatsToRemove,
      neededUsersToSelect: selectedSeatsToRemove - unusedSeats
    });
  }

  private handleRequireConfirmation(usersToRemove: string[] = []) {
    this.setState({
      usersToRemove,
      confirmingRemoval: true
    });
  }

  render(): ReactNode {
    const { visible, seatsPurchased, seatsUsed, cycleEndDate, cadence, accountId, alreadyRemovedUsers } = this.props;
    const { selectedSeatsToRemove, neededUsersToSelect, confirmingRemoval, usersToRemove } = this.state;

    if (confirmingRemoval) {
      return <ConfirmRemovalModal
        cadence={cadence}
        cycleEndDate={cycleEndDate}
        onCancel={this.handleCancel}
        seatsPurchased={seatsPurchased}
        seatsToRemove={selectedSeatsToRemove}
        usersToRemove={usersToRemove}
        visible={visible}
        accountId={accountId}
      />;
    }

    if (seatsUsed < seatsPurchased && neededUsersToSelect === 0) {
      return <RemoveUnusedSeatsModal
        visible={visible}
        seatsPurchased={seatsPurchased}
        seatsUsed={seatsUsed}
        handleSelectUsers={this.selectUsersToRemove}
        handleCancel={this.handleCancel}
        handleRequireConfirmation={this.handleRequireConfirmation}
      />;
    }

    return <RemoveUsedSeatsModal
      visible={visible}
      handleCancel={this.handleCancel}
      seatsToRemove={(seatsUsed === seatsPurchased) ? null : selectedSeatsToRemove}
      usersToSelect={(seatsUsed === seatsPurchased) ? null : neededUsersToSelect}
      cycleEndDate={cycleEndDate}
      handleRequireConfirmation={this.handleRequireConfirmation}
      alreadyRemovedUsers={alreadyRemovedUsers}
    />;
  }
}

const mapStateToProps = (state: RootState, props: IExternalProps): IMapStateProps => {
  const { accountId } = props;
  const account = accountListSelectors.getAccountById(state, accountId);
  const users = usersListSelectors.getList(state);
  let seatsPurchased = 0;
  let seatsUsed = 0;
  let cycleEndDate = 0;
  let cadence = CadenceTypesValues.ANNUALLY;
  const alreadyRemovedUsers: string[] = [];
  if (account) {
    seatsPurchased = account.seatsPurchased;
    seatsUsed = account.seatsUsed;
    cycleEndDate = account.subscription?.cycleEndDate || 0;
    cadence = account.subscription?.cycle;
    if (account.subscription && account.subscription?.usersToDisableAtEndOfCycle) {
      alreadyRemovedUsers.push(...account.subscription.usersToDisableAtEndOfCycle);
    }
  }
  return {
    seatsPurchased,
    seatsUsed,
    users,
    cycleEndDate,
    cadence,
    alreadyRemovedUsers
  };
};

export const RemovePaidSeats = connect(mapStateToProps)(RemovePaidSeatsBase);
